import {
  Button,
  ComboBox,
  Form,
  FlexGrid as Grid,
  InlineNotification,
  TextInput,
  TextInputSkeleton,
  Tile,
  Layer,
} from '@carbon/react';
import { FormRow, FormSection } from 'components/forms';
import { Information, FolderAdd, CubeView } from '@carbon/react/icons';
import { structureGridCollectionForm } from './util';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import styles from './structure-grids-collection.form.module.scss';
import { AssignStructureGrids } from './assign-structure-grids';
import { rootResourceAtom } from 'atoms/root';
import { TextAreaField } from 'components/form-fields/text-area-field';
import { getFormErrorsAtom, getFormIsInvalidAtom } from 'atoms/form-atoms';
import { FormErrorList } from 'components/form-error-list';
import { structureGridCollectionCreateAtom } from './atoms';

const errorMessageAtom = atom<string[]>([]);
import { useBasins } from '../hooks';
import { useHistory } from 'react-router-dom';

export const AddStructureGridsCollectionForm = () => {
  const { basins, basinsFetchError, basinsFetchLoading } = useBasins();

  const root = useAtomValue(rootResourceAtom);
  const userName = root?.session?.profile?.name;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const form = useMemo(() => atom(structureGridCollectionForm(userName)), []);
  const formValues = useAtomValue(form);

  const invalidFormAtom = useMemo(() => {
    return getFormIsInvalidAtom(form as any);
  }, [form]);

  const isFormInvalid = useAtomValue(invalidFormAtom);

  const [basin, setBasin] = useAtom(formValues.basin);
  const [collection, setCollection] = useAtom(formValues.collection);
  const [collectionName, setCollectionName] = useAtom(formValues.name);
  const [owner, setOwner] = useAtom(formValues.owner);
  const comment = useAtomValue(formValues.comment);
  const setStructureGridCollectionCreate = useSetAtom(
    structureGridCollectionCreateAtom
  );
  const setErrorMessage = useSetAtom(errorMessageAtom);

  useEffect(() => {
    setErrorMessage([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const history = useHistory();

  const handleCreateStructureGridCollection = () => {
    setStructureGridCollectionCreate({
      url: '/structure_grid_collections',
      type: 'POST',
      onSuccess: (response) => {
        history.push(`/structure_grid_collections/${response.id}`);
      },
      onError: (error) => {
        const errorMessages: string[] = [];

        if (
          error &&
          typeof error === 'object' &&
          'errors' in error &&
          Array.isArray(error.errors)
        ) {
          error.errors.forEach((err: unknown) => {
            if (
              err &&
              typeof err === 'object' &&
              'message' in err &&
              typeof err.message === 'string'
            ) {
              errorMessages.push(err.message);
            }
          });
        }

        // If no messages were extracted, use default message
        if (errorMessages.length === 0) {
          errorMessages.push('Structure Grid Collection creation failed');
        }

        setErrorMessage(errorMessages);
      },
      data: {
        structureGridCollection: {
          basin: basin.value,
          collectionName: collectionName.value,
          owner: owner.value === '' ? null : owner.value,
          notes: comment.value === '' ? null : comment.value,
          structureGridCollectionItemsAttributes: Array.from(
            collection.value
          ).map(([_key, value]) => ({
            structureGridId: value.id,
          })),
        },
      },
    });
  };

  return (
    <>
      <Grid className={styles.form}>
        <Tile>
          <Form>
            {basinsFetchError && (
              <FormRow>
                <InlineNotification
                  kind="error"
                  title="Error fetching geologic intervals"
                  subtitle=""
                />
              </FormRow>
            )}
            <FormSection
              title="General Structure Grid Collection Information"
              iconComponent={<Information />}
            >
              <FormRow>
                {basinsFetchLoading ? (
                  <TextInputSkeleton hideLabel />
                ) : (
                  <ComboBox
                    titleText="Basin"
                    invalid={!basin.isValid}
                    invalidText={!basin.isValid ? basin.error : ''}
                    id="basin"
                    items={basins}
                    placeholder="Select Basin"
                    onChange={({ selectedItem }) => {
                      setBasin(selectedItem ?? '');
                      setCollection(new Map());
                    }}
                  ></ComboBox>
                )}
              </FormRow>
            </FormSection>
            <FormSection
              title="Assign Structure Grids"
              iconComponent={<CubeView />}
            >
              <AssignStructureGrids
                basinField={formValues.basin}
                collectionField={formValues.collection}
              />
            </FormSection>
            <FormSection
              title="Collection Information"
              iconComponent={<FolderAdd />}
            >
              <FormRow className={styles.nameFormRow}>
                <Layer>
                  <TextInput
                    id={'name'}
                    labelText={
                      <label>{'Structure Grid Collection Name'}</label>
                    }
                    size="md"
                    value={collectionName.value || ''}
                    onChange={({ target: { value } }) =>
                      setCollectionName(value)
                    }
                    invalid={!collectionName.isValid}
                    invalidText={
                      !collectionName.isValid ? collectionName.error : ''
                    }
                  />
                </Layer>
                <Layer>
                  <TextInput
                    id={'owner'}
                    labelText={<label>{'Owner'}</label>}
                    size="md"
                    value={owner.value || ''}
                    onChange={({ target: { value } }) => setOwner(value)}
                    invalid={!owner.isValid}
                    invalidText={!owner.isValid ? owner.error : ''}
                  />
                </Layer>
              </FormRow>
              <FormRow>
                <TextAreaField
                  fieldAtom={formValues.comment}
                  id="comment"
                  labelText="Comments (optional)"
                  placeholder="Comments for Structure Grid Collection"
                />
              </FormRow>
            </FormSection>
            <FormRow>
              <FormErrorList
                hideCloseButton
                errorsAtom={getFormErrorsAtom(form as any)}
              />
              <FormErrorList hideCloseButton errorsAtom={errorMessageAtom} />
            </FormRow>
            <FormRow className={styles.buttons}>
              <Button
                kind="tertiary"
                onClick={() => history.push('/structure_grid_collections')}
              >
                Cancel
              </Button>
              <Button
                disabled={isFormInvalid}
                onClick={() => handleCreateStructureGridCollection()}
              >
                Create Structure Grid Collection
              </Button>
            </FormRow>
          </Form>
        </Tile>
      </Grid>
    </>
  );
};
